// Import Swiper styles
import "swiper/css";
import "swiper/css/a11y";
import "swiper/css/navigation";
import "../../../theme/SwiperOverrides.css";

import * as styles from "./SectionSpotlightDogs.module.scss";

import React, { useEffect } from "react";
import { Button } from "../../../components/common";

import { useMemo } from "react";
import DogCardCarousel from "../../../components/DogCardCarousel/DogCardCarousel";
import { useDogSearchApi } from "../../../context";
import { Colours } from "../../../utils/colours";
import {
	mapEsDogDocToDogCarouselCardProps,
	mapNodeDogToEsDogDoc,
} from "../../../utils/mappers";
import WrappedSection from "../../WrappedSection/WrappedSection";

export interface SectionSpotlightDogsProps {
	title: string;
	htmlBody?: string;
	link?: DogsTrust.Link;
	items: DogsTrust.DogData[];
	minimumItems?: number;
	jumpLinksTitle?: string;
}

export const getSectionSpotlightDogsProps = ({
	promotedLinks,
	field_front_end_title,
	field_section_short_description,
	field_link,
	field_min_items_to_display,
	field_jump_links_title,
}: Queries.SectionDataFragment) => ({
	title: field_front_end_title,
	htmlBody: field_section_short_description?.value,
	link: field_link?.uri && {
		url: field_link.uri,
		title: field_link.title,
	},
	items: promotedLinks
		?.map(mapNodeDogToEsDogDoc)
		.map(mapEsDogDocToDogCarouselCardProps),
	minimumItems: field_min_items_to_display,
	jumpLinksTitle: field_jump_links_title,
});
export const SectionSpotlightDogs: React.FC<SectionSpotlightDogsProps> = ({
	title,
	htmlBody,
	link,
	items,
	minimumItems,
	jumpLinksTitle,
}) => {
	const { useDogSearchQuery, results } = useDogSearchApi();

	// biome-ignore lint/correctness/useExhaustiveDependencies: We only want this to run once
	useEffect(() => {
		useDogSearchQuery();
	}, []);

	const itemsToDisplay = useMemo(() => {
		let itemsToReturn = [...items];
		const minItems = minimumItems || 16;
		if (itemsToReturn.length < minItems && results?.length > 0) {
			itemsToReturn = [...itemsToReturn, ...results].slice(
				0,
				minItems - itemsToReturn.length,
			);
		}
		return itemsToReturn.sort((a) => (a.media?.images?.[0]?.src ? -1 : 0));
	}, [results, items, minimumItems]);

	return (
		<WrappedSection
			sectionName="SectionSpotlightDogs"
			sectionColour={Colours.SUNSHINE_YELLOW}
		>
			<div className={styles.spotlightdogsinner}>
				<div className={styles.heading}>
					{jumpLinksTitle ? (
						<h2 className={styles.title} data-jumplinkstitle={jumpLinksTitle}>
							{title}
						</h2>
					) : (
						<h2 className={styles.title}>{title}</h2>
					)}
				</div>
				{htmlBody?.length && (
					<div
						className={styles.introtext}
						// biome-ignore lint/security/noDangerouslySetInnerHtml: Safe source
						dangerouslySetInnerHTML={{
							__html: htmlBody,
						}}
					/>
				)}
				<DogCardCarousel dogs={itemsToDisplay} />
				<Button
					className={styles.showalldogs}
					variant={"tertiary"}
					ariaLabel="link to our dogs"
					fullWidth={true}
					url={link?.url || "/rehoming/dogs"}
				>
					{link?.title || "Show all dogs"}
				</Button>
			</div>
		</WrappedSection>
	);
};

export default SectionSpotlightDogs;
